import _isEqual from 'lodash/isEqual'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import { memo, useMemo, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import UiKitAccordionList from '@nxt-insurance/ui-kit/components/listComponents/AccordionList/index.next'
import { borderRadius } from '@nxt-insurance/ui-kit/styles/borderRadius'
import { bgColor, borderColor } from '@nxt-insurance/ui-kit/styles/colors'
import type { Color } from '@nxt-insurance/ui-kit/types'

import { sanityClientLike } from '@/cms/client'
import type { HorizontalAlignment } from '@/cms/helperTypes'
import { borderRadiusEnum, colorEnum, iconSchema, spaceEnum } from '@/cms/helperTypes'
import { imageSchema } from '@/cms/image'
import { Icon } from '@/components/Icon'
import { BlockContent } from '@/components/renderers/BlockContent'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { getContrastColor } from '@/utils/style'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

import { ConfigurableTypography, configurableTypography } from './ConfigurableTypography'

const accordionListSchema = z.object({
  _key: z.string().optional(),
  _type: z.literal('accordionList'),
  borderColor: colorEnum.optional().nullable(),
  backgroundColor: colorEnum.optional().nullable(),
  fullContainerWidth: z.boolean().optional().nullable(),
  borderRadius: borderRadiusEnum.optional().nullable(),
  padding: spaceEnum.optional().nullable(),
  rowDividerBordered: z.boolean().optional().nullable(),
  items: z
    .array(
      z.union([
        z.object({
          _key: z.string(),
          title: z.string(),
          subtitle: z.string().optional(),
          content: z.array(z.unknown()).optional(),
          icon: iconSchema.optional(),
          image: imageSchema
            .merge(
              z.object({
                width: z.number(),
                alt: z.string(),
              })
            )
            .optional(),
        }),
        z.object({
          _key: z.string(),
          titleButton: z.discriminatedUnion('variant', [
            z.object({
              variant: z.literal('bordered'),
              label: configurableTypography,
              borderColor: colorEnum,
              borderRadius: borderRadiusEnum,
            }),
            z.object({
              variant: z.literal('filled'),
              label: configurableTypography,
              backgroundColor: colorEnum,
              borderRadius: borderRadiusEnum,
            }),
            z.object({
              variant: z.literal('default'),
              label: configurableTypography,
            }),
          ]),
          content: z.array(z.unknown()).optional(),
        }),
      ])
    )
    .nullable(),
})

type Node = z.infer<typeof accordionListSchema>
type Item = NonNullable<Node['items']>[number]
type ItemWithTitle = Extract<Item, { title: string }>

const ImageOrIcon = (props: { image: ItemWithTitle['image']; icon: ItemWithTitle['icon'] }) => {
  const imageProps = useNextSanityImage(sanityClientLike, props.image?.asset ?? null, {
    imageBuilder: (builder) => builder.width((props.image?.width ?? 0) * 2),
  })

  if (imageProps && props.image) {
    return (
      <Image
        {...imageProps}
        width={props.image.width}
        height={imageProps.height / 2}
        alt={props.image.alt}
      />
    )
  }

  if (props.icon) {
    return <Icon icon={props.icon} size="small" />
  }

  return null
}

const AccordionListItem = ({
  item,
  list,
  width,
  align,
  sectionName,
  sectionColor,
  rowDividerBordered,
}: {
  item: Item
  list: Node
  width: number
  align: HorizontalAlignment
  sectionName: string
  sectionColor?: Color
  rowDividerBordered?: boolean
}) => {
  const titleButtonProps = useMemo(() => {
    if ('title' in item) {
      return {
        className: twMerge('bg-transparent border-transparent rounded-none p-0'),
      }
    }
    switch (item.titleButton.variant) {
      case 'bordered': {
        return {
          className: twMerge(
            'bg-transparent py-4 pr-2.5 pb-4',
            borderColor({
              color: item.titleButton.borderColor,
            }),
            borderRadius({
              radius: item.titleButton.borderRadius,
            })
          ),
        }
      }
      case 'filled': {
        return {
          className: twMerge(
            'bg-transparent py-4 pr-2.5 pb-4',
            borderColor({
              color: item.titleButton.backgroundColor,
            }),
            bgColor({
              color: item.titleButton.backgroundColor,
            }),
            borderRadius({
              radius: item.titleButton.borderRadius,
            })
          ),
        }
      }
      default: {
        return {
          className: twMerge('bg-transparent border-transparent rounded-none p-0'),
        }
      }
    }
  }, [item])
  return (
    <UiKitAccordionList.Item
      key={item._key}
      value={item._key}
      isExpandable={Boolean(item.content)}
      backgroundColor={list.backgroundColor ?? undefined}
      borderColor={list.borderColor ?? undefined}
      borderRadius={list.borderRadius ?? undefined}
      padding={list.padding ?? undefined}
      rowDividerBordered={rowDividerBordered ?? undefined}
    >
      {'title' in item ? (
        <UiKitAccordionList.Trigger>
          <ImageOrIcon image={item.image} icon={item.icon} />
          <UiKitAccordionList.HeaderContainer>
            <UiKitAccordionList.Title>{item.title}</UiKitAccordionList.Title>
            {item.subtitle ? (
              <UiKitAccordionList.Subtitle>{item.subtitle}</UiKitAccordionList.Subtitle>
            ) : null}
          </UiKitAccordionList.HeaderContainer>
          {item.content ? (
            <UiKitAccordionList.TriggerStateIconContainer>
              <Icon
                size="small"
                icon={{
                  name: getContrastColor(
                    list.backgroundColor ?? sectionColor ?? 'uiLight'
                  ).includes('Light')
                    ? 'action.chevronDownUiLight_24'
                    : 'action.chevronDown_24',
                }}
              />
            </UiKitAccordionList.TriggerStateIconContainer>
          ) : null}
        </UiKitAccordionList.Trigger>
      ) : (
        <UiKitAccordionList.Trigger>
          <span
            className={twMerge(
              'border-1 flex w-full flex-row items-center justify-center gap-x-2.5 border',
              titleButtonProps.className
            )}
          >
            <ConfigurableTypography
              node={{
                index: 0,
                isInline: false,
                renderNode: () => null,
                value: { _key: '1', ...item.titleButton.label },
              }}
              align={item.titleButton.label.textAlign ?? 'left'}
            />
            <UiKitAccordionList.TriggerStateIconContainer>
              <Icon
                size="xsmall"
                icon={{
                  name: getContrastColor(
                    'backgroundColor' in item.titleButton && item.titleButton.backgroundColor
                      ? item.titleButton.backgroundColor ?? sectionColor
                      : sectionColor ?? 'uiLight'
                  ).includes('Light')
                    ? 'action.chevronDownUiLight_16'
                    : 'action.chevronDownUiLight_16',
                }}
              />
            </UiKitAccordionList.TriggerStateIconContainer>
          </span>
        </UiKitAccordionList.Trigger>
      )}
      {item.content ? (
        <UiKitAccordionList.Content withIcon={'title' in item && Boolean(item.icon)}>
          <BlockContent
            blocks={item.content}
            width={width}
            align={align}
            sectionName={sectionName}
          />
        </UiKitAccordionList.Content>
      ) : null}
    </UiKitAccordionList.Item>
  )
}

export const AccordionList = memo(
  (props: {
    node: PortableTextTypeComponentProps<unknown>
    align: HorizontalAlignment
    width: number
    sectionName: string
    sectionColor?: Color
    rowDividerBordered?: boolean
  }) => {
    const value = useMemo(() => accordionListSchema.parse(props.node.value), [props.node.value])
    const { track } = useAnalytics()
    const previousValue = useRef<string[] | null>([])
    return (
      <UiKitAccordionList.Root
        fullContainerWidth={value.fullContainerWidth ?? false}
        type="multiple"
        rowDividerBordered={value.rowDividerBordered ?? false}
        borderColor={value.borderColor ?? undefined}
        onValueChange={(newValue) => {
          const diffKey = newValue
            .filter((v) => !previousValue.current?.includes(v))
            .concat((previousValue.current ?? []).filter((v) => !newValue.includes(v)))[0]

          previousValue.current = newValue
          if (!diffKey || !value.items) {
            return
          }
          const item = value.items.find((it) => it._key === diffKey)
          if (!item?.content) {
            return
          }
          const analyticsData = {
            isExpanded: newValue.includes(diffKey),
            content: item.content,
            sectionName: props.sectionName,
            url: window.location.href,
          }
          track(EVENTS.AccordionItemToggled, analyticsData)
        }}
      >
        {value.items?.map((item) => (
          <AccordionListItem
            key={item._key}
            item={item}
            list={value}
            width={props.width}
            align={props.align}
            sectionName={props.sectionName}
            sectionColor={props.sectionColor}
            rowDividerBordered={value.rowDividerBordered ?? false}
          />
        ))}
      </UiKitAccordionList.Root>
    )
  },
  _isEqual
)
